import * as React from 'react';
import PropTypes from 'prop-types';
import {
    makeStyles, Accordion, AccordionSummary, Typography, AccordionDetails,
} from '@material-ui/core';
import { CheckCircleRounded } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';

import { fillArrayToLength } from '../../../../arrayUtils';
import AsyncTextField from '../../../AsyncTextField';

const useStyles = makeStyles(theme => ({
    accordionDetails: {
        padding: theme.spacing(3, 2),
        gap: theme.spacing(3, 2),
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1),
            gap: theme.spacing(2, 1),
        },
    },
    rowHeader: {
        backgroundColor: theme.palette.grey[50],
    },
    rowFilledIcon: {
        position: 'absolute',
        right: theme.spacing(1.5),
        color: theme.youwell.colors.green,
    },
    cell: {
        width: 'calc((100% - (3 * 16px))/4)',
        padding: theme.spacing(0.5),

        [theme.breakpoints.down('xs')]: {
            width: 'calc((100% - (2 * 8px))/3)',
        },
    },
    cellHeader: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[700],
        marginBottom: theme.spacing(1),
        padding: theme.spacing(0.5),
    },
    cellTextField: {
        marginTop: theme.spacing(1),
    },
}));


const ColumnQuestions = ({
    header, cellQuestions, onChange, responses, readOnly,
}) => {
    const classes = useStyles();
    const cellData = responses ? JSON.parse(responses) : {};

    const handleQuestionChange = questionIndex => (updatedValue) => {
        if (readOnly) return;
        const updatedData = {
            ...cellData,
            [questionIndex]: updatedValue,
        };
        onChange(updatedData);
    };

    return (
        <div className={classes.cell}>
            <Typography className={classes.cellHeader} variant="body2">
                {header}
            </Typography>
            {cellQuestions.map((question, questionIndex) => (
                <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={questionIndex}
                >
                    <AsyncTextField
                        label={question}
                        value={cellData[questionIndex] || ''}
                        onChange={handleQuestionChange(questionIndex)}
                        className={classes.cellTextField}
                        color="secondary"
                        readOnly={readOnly}
                        shrink
                    />
                </div>
            ))}
        </div>
    );
};

ColumnQuestions.propTypes = {
    header: PropTypes.string.isRequired,
    cellQuestions: PropTypes.array.isRequired,
    responses: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

ColumnQuestions.defaultProps = {
    readOnly: false,
}

const TableRow = ({
    expanded, onChange, onClick, columnHeaders, cellQuestions, header, responses, readOnly,
}) => {
    const classes = useStyles();
    const translate = useSelector(state => getTranslate(state.localize));
    const rowData = fillArrayToLength(responses, columnHeaders.length, '');

    const cellCheck = (cellData, compareLength) => {
        if (!cellData) return false;

        const cellDataValues = Object.values(JSON.parse(cellData));
        if (cellDataValues.length !== compareLength || cellDataValues.includes('')) return false;

        return true;
    }

    const rowIsFilled = rowData.every(cellData => cellCheck(cellData, cellQuestions.length));

    const handleCellChange = columnIndex => (data) => {
        const updatedRowData = rowData.map((row, i) => (
            i === columnIndex ? JSON.stringify(data) : row
        ));
        onChange(updatedRowData);
    }

    return (
        <Accordion
            onChange={onClick}
            expanded={expanded}
        >
            <AccordionSummary className={classes.rowHeader}>
                <Typography color="primary" variant="subtitle2">{header}</Typography>
                {rowIsFilled && <CheckCircleRounded className={classes.rowFilledIcon} titleAccess={translate('common.completed')} />}
            </AccordionSummary>

            <AccordionDetails className={classes.accordionDetails}>
                {columnHeaders.map((columnHeader, columnIndex) => (
                    <ColumnQuestions
                        // eslint-disable-next-line react/no-array-index-key
                        key={columnIndex}
                        header={columnHeader}
                        cellQuestions={cellQuestions}
                        responses={rowData[columnIndex] || ''}
                        onChange={handleCellChange(columnIndex)}
                        readOnly={readOnly}
                    />
                ))}
            </AccordionDetails>
        </Accordion>
    )
}

TableRow.propTypes = {
    expanded: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    header: PropTypes.string.isRequired,
    columnHeaders: PropTypes.array.isRequired,
    cellQuestions: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    responses: PropTypes.array.isRequired,
    readOnly: PropTypes.bool,
}

TableRow.defaultProps = {
    readOnly: false,
}

const CollapsedTable = ({ question, handleResponseUpdate, responses, readOnly }) => {
    const classes = useStyles();
    const [expandedIndex, setExpandedIndex] = React.useState(-1);

    if (!question.tableConfig) return null;

    const rowHeaders = question.tableConfig.rowHeaders || [];
    const columnHeaders = question.tableConfig.columnHeaders || [];
    const cellQuestions = question.tableConfig.cellQuestions || [];
    const tableData = fillArrayToLength(responses?.tableResponses, rowHeaders.length, []);

    const handleRowChange = rowIndex => (rowData) => {
        const updatedTableData = tableData.map((row, ri) => (
            ri === rowIndex ? rowData : row
        ));

        handleResponseUpdate('tableResponses', updatedTableData);
    }

    return (
        <div className={classes.root}>
            {rowHeaders.map((rowHeader, rowIndex) => (
                <TableRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={rowIndex}
                    expanded={expandedIndex === rowIndex}
                    onClick={() => setExpandedIndex(expandedIndex === rowIndex ? -1 : rowIndex)}
                    header={rowHeader}
                    columnHeaders={columnHeaders}
                    cellQuestions={cellQuestions}
                    onChange={handleRowChange(rowIndex)}
                    responses={tableData[rowIndex] || []}
                    readOnly={readOnly}
                />
            ))}
        </div>
    );
};

CollapsedTable.propTypes = {
    question: PropTypes.object.isRequired,
    handleResponseUpdate: PropTypes.func.isRequired,
    responses: PropTypes.object,
    readOnly: PropTypes.bool,
};

CollapsedTable.defaultProps = {
    responses: null,
    readOnly: false,
};

export default CollapsedTable;
